import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, CheckCircle2, AlertTriangle, FileWarning, Copy, Check } from "lucide-react";
import { runFontAudit, type FontAuditResult, type FontViolation, type ViolationContext } from "@/lib/font-audit";

export const Route = createFileRoute("/_admin/admin/font-audit")({
  component: FontAuditPage,
});

type StyleSpec = {
  id: string;
  label: string;
  utility: string;
  description: string;
  className: string;
  sample: string;
};

const STYLE_SPECS: StyleSpec[] = [
  {
    id: "h1",
    label: "H1 – Hero / Seiten-Titel",
    utility: "heading-h1",
    description: "Hero- und Seiten-H1. Funnel Display 800.",
    className: "heading-h1 text-primary",
    sample: "Wahlen gewinnen, wenn es zählt.",
  },
  {
    id: "h2",
    label: "H2 – Sektionsüberschrift",
    utility: "heading-h2",
    description: "Sektions-H2 auf allen Content-Seiten.",
    className: "heading-h2 text-primary",
    sample: "Bausteine Ihres Wahlerfolgs",
  },
  {
    id: "h3",
    label: "H3 – Karten- / Sub-Überschrift",
    utility: "heading-h3",
    description: "Karten, Timeline-Schritte, Sub-Überschriften.",
    className: "heading-h3 text-primary",
    sample: "Direkter Kontakt zur Wählerschaft",
  },
  {
    id: "hero-serif",
    label: "Hero-Serif-Einsprengsel",
    utility: "font-body font-normal italic",
    description: "Merriweather-Einsprengsel in Hero-Headlines (einzige Ausnahme).",
    className: "font-body font-normal italic text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-primary",
    sample: "vertrauen uns.",
  },
  {
    id: "lead",
    label: "Lead / Intro-Absatz",
    utility: "text-lg text-muted-foreground",
    description: "Einleitender Absatz unter H1/H2.",
    className: "text-lg text-muted-foreground",
    sample: "Wir begleiten Kandidatinnen und Kandidaten von der Strategie bis zum Wahlabend.",
  },
  {
    id: "body",
    label: "Body – Fließtext (Merriweather)",
    utility: "font-body",
    description: "Standard-Fließtext, Merriweather Regular.",
    className: "font-body text-base text-foreground",
    sample: "Unsere Arbeit beruht auf klarer Positionierung, präziser Ansprache und konsequenter Umsetzung.",
  },
  {
    id: "body-display",
    label: "Body Display (Funnel)",
    utility: "font-display",
    description: "UI-Text, Labels, Meta-Informationen in Funnel Display.",
    className: "font-display text-base text-foreground",
    sample: "Simon Mai freut sich auf das Gespräch mit Ihnen.",
  },
  {
    id: "small",
    label: "Small / Meta",
    utility: "text-sm text-muted-foreground",
    description: "Meta-Angaben, Bildunterschriften, Hilfetexte.",
    className: "text-sm text-muted-foreground",
    sample: "Lesezeit 4 Minuten · Aktualisiert Juli 2026",
  },
  {
    id: "xs",
    label: "Extra Small / Chips",
    utility: "text-xs",
    description: "Chips, Tags, Fußnoten.",
    className: "text-xs text-muted-foreground",
    sample: "Kategorie: Politische Kommunikation",
  },
  {
    id: "button",
    label: "Button-Label",
    utility: "font-display font-extrabold",
    description: "Primärer CTA-Button (Brand-Gelb).",
    className: "inline-flex items-center rounded-md bg-brand-button px-5 py-2.5 font-display text-sm font-extrabold text-primary",
    sample: "Erstgespräch vereinbaren",
  },
  {
    id: "link",
    label: "Textlink mit Pfeil",
    utility: "inline-flex gap-1.5",
    description: "Standalone-CTA-Link mit ArrowUpRight-Icon.",
    className: "inline-flex items-center gap-1.5 font-display font-extrabold text-primary underline underline-offset-4",
    sample: "Agentur kennenlernen",
  },
];

type Computed = {
  family: string;
  sizePx: number;
  weight: string;
  lineHeight: string;
  letterSpacing: string;
};

function shortFamily(f: string): string {
  const first = f.split(",")[0]?.trim().replace(/^["']|["']$/g, "") ?? f;
  return first;
}

function StyleCard({
  spec,
  onMeasured,
}: {
  spec: StyleSpec;
  onMeasured?: (id: string, m: { mobile: Computed | null; desktop: Computed | null }) => void;
}) {
  const mobileRef = useRef<HTMLDivElement | null>(null);
  const desktopRef = useRef<HTMLDivElement | null>(null);
  const [mobile, setMobile] = useState<Computed | null>(null);
  const [desktop, setDesktop] = useState<Computed | null>(null);
  const [text, setText] = useState(spec.sample);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    function read(el: HTMLElement | null): Computed | null {
      if (!el) return null;
      const cs = window.getComputedStyle(el);
      return {
        family: cs.fontFamily,
        sizePx: parseFloat(cs.fontSize),
        weight: cs.fontWeight,
        lineHeight: cs.lineHeight,
        letterSpacing: cs.letterSpacing,
      };
    }
    const m = read(mobileRef.current);
    const d = read(desktopRef.current);
    setMobile(m);
    setDesktop(d);
    onMeasured?.(spec.id, { mobile: m, desktop: d });
  }, [text, spec.id, onMeasured]);


  const Meta = ({ c }: { c: Computed | null }) =>
    c ? (
      <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono text-[11px] text-muted-foreground">
        <dt>Font</dt><dd className="text-foreground">{shortFamily(c.family)}</dd>
        <dt>Size</dt><dd className="text-foreground">{c.sizePx.toFixed(2)}px · {(c.sizePx / 16).toFixed(3)}rem</dd>
        <dt>Weight</dt><dd className="text-foreground">{c.weight}</dd>
        <dt>Line</dt><dd className="text-foreground">{c.lineHeight}</dd>
        <dt>Track</dt><dd className="text-foreground">{c.letterSpacing}</dd>
      </dl>
    ) : (
      <p className="font-mono text-[11px] text-muted-foreground">–</p>
    );

  return (
    <li className="rounded-lg border border-border bg-background p-5">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-display text-sm font-extrabold text-primary">{spec.label}</p>
          <p className="mt-1 text-xs text-muted-foreground">{spec.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <code className="rounded bg-secondary px-2 py-1 font-mono text-[11px] text-secondary-foreground">
            {spec.utility}
          </code>
          <button
            type="button"
            onClick={() => setEditing((e) => !e)}
            className="rounded border border-border px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"
          >
            {editing ? "Fertig" : "Text testen"}
          </button>
        </div>
      </div>

      {editing && (
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          className="mt-3 w-full rounded border border-border bg-background p-2 font-mono text-xs"
        />
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="rounded-md border border-dashed border-border p-4">
          <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            Mobile · 375px
          </p>
          <div style={{ width: 343 }} className="max-w-full overflow-hidden">
            <div ref={mobileRef} className={spec.className}>
              {spec.id === "link" ? (
                <>
                  {text}
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </>
              ) : (
                text
              )}
            </div>
          </div>
          <div className="mt-3">
            <Meta c={mobile} />
          </div>
        </div>
        <div className="rounded-md border border-dashed border-border p-4">
          <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            Desktop · ≥1024px
          </p>
          <div className="lg-preview">
            <div ref={desktopRef} className={spec.className}>
              {spec.id === "link" ? (
                <>
                  {text}
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </>
              ) : (
                text
              )}
            </div>
          </div>
          <div className="mt-3">
            <Meta c={desktop} />
          </div>
        </div>
      </div>
    </li>
  );
}

type Measurements = Record<string, { mobile: Computed | null; desktop: Computed | null }>;

function csvCell(v: string | number | null | undefined): string {
  const s = v == null ? "" : String(v);
  return `"${s.replace(/"/g, '""')}"`;
}

function fmtSize(c: Computed | null): { px: string; rem: string } {
  if (!c) return { px: "", rem: "" };
  return { px: c.sizePx.toFixed(2), rem: (c.sizePx / 16).toFixed(3) };
}

function buildCsv(measurements: Measurements): string {
  const headers = [
    "ID","Label","Utility","Beispieltext",
    "Font (Mobile)","Größe px (Mobile)","Größe rem (Mobile)","Weight (Mobile)","Line-Height (Mobile)","Letter-Spacing (Mobile)",
    "Font (Desktop)","Größe px (Desktop)","Größe rem (Desktop)","Weight (Desktop)","Line-Height (Desktop)","Letter-Spacing (Desktop)",
  ];
  const rows = STYLE_SPECS.map((s) => {
    const m = measurements[s.id]?.mobile ?? null;
    const d = measurements[s.id]?.desktop ?? null;
    const ms = fmtSize(m);
    const ds = fmtSize(d);
    return [
      s.id, s.label, s.utility, s.sample,
      m ? shortFamily(m.family) : "", ms.px, ms.rem, m?.weight ?? "", m?.lineHeight ?? "", m?.letterSpacing ?? "",
      d ? shortFamily(d.family) : "", ds.px, ds.rem, d?.weight ?? "", d?.lineHeight ?? "", d?.letterSpacing ?? "",
    ].map(csvCell).join(";");
  });
  return "\uFEFF" + [headers.map(csvCell).join(";"), ...rows].join("\r\n");
}

function downloadCsv(measurements: Measurements) {
  const blob = new Blob([buildCsv(measurements)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "typografie-skala.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));
}

function openPdfWindow(measurements: Measurements) {
  const win = window.open("", "_blank", "width=900,height=1200");
  if (!win) return;
  const date = new Date().toLocaleDateString("de-DE", { year: "numeric", month: "long", day: "numeric" });
  const blocks = STYLE_SPECS.map((s) => {
    const m = measurements[s.id]?.mobile ?? null;
    const d = measurements[s.id]?.desktop ?? null;
    const inlineStyle = m
      ? `font-family:${m.family};font-size:${m.sizePx}px;font-weight:${m.weight};line-height:${m.lineHeight};letter-spacing:${m.letterSpacing};color:#004b51;`
      : "";
    const row = (label: string, c: Computed | null) => {
      if (!c) return `<tr><th>${label}</th><td colspan="5">–</td></tr>`;
      return `<tr><th>${label}</th><td>${esc(shortFamily(c.family))}</td><td>${c.sizePx.toFixed(2)}px · ${(c.sizePx / 16).toFixed(3)}rem</td><td>${esc(c.weight)}</td><td>${esc(c.lineHeight)}</td><td>${esc(c.letterSpacing)}</td></tr>`;
    };
    return `
      <section class="block">
        <header>
          <h2>${esc(s.label)}</h2>
          <code>${esc(s.utility)}</code>
        </header>
        <p class="desc">${esc(s.description)}</p>
        <div class="sample" style="${inlineStyle}">${esc(s.sample)}</div>
        <table>
          <thead><tr><th></th><th>Font</th><th>Größe</th><th>Weight</th><th>Line-Height</th><th>Letter-Spacing</th></tr></thead>
          <tbody>${row("Mobile · 375px", m)}${row("Desktop · ≥1024px", d)}</tbody>
        </table>
      </section>
    `;
  }).join("");

  win.document.write(`<!doctype html>
<html lang="de"><head><meta charset="utf-8"><title>Typografie-Skala – creategy</title>
<style>
  @page { size: A4; margin: 18mm 14mm; }
  body { font-family: -apple-system, "Segoe UI", Arial, sans-serif; color: #212121; margin: 0; padding: 24px; }
  h1 { font-size: 22px; margin: 0 0 4px; color: #004b51; }
  .meta { font-size: 12px; color: #666; margin-bottom: 24px; }
  .block { border-top: 1px solid #ddd; padding: 16px 0; page-break-inside: avoid; }
  .block header { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }
  .block h2 { font-size: 14px; margin: 0; color: #004b51; }
  .block code { font-size: 11px; background: #f3f3f0; padding: 2px 6px; border-radius: 3px; }
  .desc { font-size: 11px; color: #666; margin: 4px 0 12px; }
  .sample { padding: 12px 0; margin: 0 0 12px; word-wrap: break-word; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; }
  th, td { border: 1px solid #e5e5e5; padding: 4px 6px; text-align: left; vertical-align: top; }
  thead th { background: #f7f7f4; font-weight: 600; }
  tbody th { width: 110px; background: #fafaf7; font-weight: 600; }
  .actions { margin-bottom: 16px; }
  .actions button { font: inherit; padding: 6px 12px; cursor: pointer; }
  @media print { .actions { display: none; } }
</style></head>
<body>
  <div class="actions"><button onclick="window.print()">Drucken / Als PDF speichern</button></div>
  <h1>Typografie-Skala – creategy</h1>
  <p class="meta">Stand: ${esc(date)} · ${STYLE_SPECS.length} Stile</p>
  ${blocks}
</body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => { try { win.print(); } catch { /* ignore */ } }, 400);
}

function TypographyScale() {
  const [measurements, setMeasurements] = useState<Measurements>({});
  const handleMeasured = useCallback(
    (id: string, m: { mobile: Computed | null; desktop: Computed | null }) => {
      setMeasurements((prev) => ({ ...prev, [id]: m }));
    },
    [],
  );
  const ready = Object.keys(measurements).length === STYLE_SPECS.length;

  return (
    <section className="mt-10">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-extrabold text-primary">Typografie-Skala</h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Visuelle Übersicht aller Überschriften- und Textstile mit realer Schriftart, -größe
            und Beispieltext. Werte werden live per <code>getComputedStyle</code> ausgelesen und
            aktualisieren sich automatisch bei Token-Änderungen.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!ready}
            onClick={() => downloadCsv(measurements)}
            title={ready ? "Als CSV exportieren" : "Werte werden geladen…"}
            className="rounded-md border border-border bg-background px-3 py-1.5 font-display text-xs font-extrabold text-primary hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
          >
            CSV exportieren
          </button>
          <button
            type="button"
            disabled={!ready}
            onClick={() => openPdfWindow(measurements)}
            title={ready ? "Druckansicht öffnen und als PDF speichern" : "Werte werden geladen…"}
            className="rounded-md bg-brand-button px-3 py-1.5 font-display text-xs font-extrabold text-primary hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            PDF drucken
          </button>
        </div>
      </div>
      <ul className="mt-6 grid gap-4">
        {STYLE_SPECS.map((s) => (
          <StyleCard key={s.id} spec={s} onMeasured={handleMeasured} />
        ))}
      </ul>
    </section>
  );
}



const RULE_LABEL: Record<FontViolation["rule"], string> = {
  "font-family": "font-family-Deklaration",
  "font-literal": "Brand-Font literal",
  "inline-style": "Inline-Style fontFamily",
  "arbitrary-class": "Arbitrary Tailwind-Klasse",
  "foreign-font": "Fremd-Font",
  "body-weight": "Fett-Utility auf Body-Font",
  "generic-family": "Generische Font-Familie",
};

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fallthrough */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

function buildViolationPrompt(
  v: FontViolation,
  ruleDescription: string,
  ruleRemedy: string,
): string {
  const ctx = v.context;
  const contextEnd = ctx.startLine + ctx.lines.length - 1;
  const numbered = ctx.lines
    .map((line, i) => {
      const lineNo = ctx.startLine + i;
      const marker = i === ctx.offendingLineIndex ? "  <<< betroffene Zeile" : "";
      return `${String(lineNo).padStart(4, " ")}: ${line}${marker}`;
    })
    .join("\n");

  return [
    "Behebe eine Font-Token-Abweichung im creategy-Projekt.",
    "",
    `Datei: ${v.file.replace(/^\//, "")}`,
    `Position: Zeile ${v.line}, Spalte ${v.column}`,
    `Regel: ${RULE_LABEL[v.rule]} (${v.rule})`,
    `Fund: \`${v.match}\``,
    `Problem: ${ruleDescription}`,
    `Erwartet: ${ruleRemedy}`,
    "",
    `Kontext (Zeilen ${ctx.startLine}–${contextEnd}):`,
    "```",
    numbered,
    "```",
    "",
    "Ersetze im o. g. Bereich ausschließlich die Font-Referenz durch die passende Design-Token-Utility (`font-display` = Funnel Display, `font-body` = Merriweather). Alle anderen Klassen, Attribute, Whitespace und der umliegende Text bleiben unverändert. Ist bereits eine passende `font-*`-Utility vorhanden, entferne die redundante Referenz ersatzlos. Hero-Serif-Einsprengsel (`font-body font-normal italic` in Hero-H1) sind zulässige Ausnahmen und dürfen bestehen bleiben.",
  ].join("\n");
}

function buildFilePrompt(
  file: string,
  items: FontViolation[],
  rulesById: Record<string, { description: string; remedy: string }>,
): string {
  const header = [
    `Behebe ${items.length} Font-Token-Abweichung(en) in einer Datei.`,
    "",
    `Datei: ${file.replace(/^\//, "")}`,
    "",
    "Fundstellen:",
  ].join("\n");
  const list = items
    .map((v, idx) => {
      const rule = rulesById[v.rule];
      const ctx = v.context;
      const contextEnd = ctx.startLine + ctx.lines.length - 1;
      const numbered = ctx.lines
        .map((line, i) => {
          const lineNo = ctx.startLine + i;
          const marker = i === ctx.offendingLineIndex ? "  <<< betroffene Zeile" : "";
          return `${String(lineNo).padStart(4, " ")}: ${line}${marker}`;
        })
        .join("\n");
      return [
        `\n${idx + 1}. Zeile ${v.line}, Spalte ${v.column} — ${RULE_LABEL[v.rule]} (${v.rule})`,
        `   Fund: \`${v.match}\``,
        `   Problem: ${rule?.description ?? ""}`,
        `   Erwartet: ${rule?.remedy ?? ""}`,
        `   Kontext (Zeilen ${ctx.startLine}–${contextEnd}):`,
        "   ```",
        numbered.split("\n").map((l) => "   " + l).join("\n"),
        "   ```",
      ].join("\n");
    })
    .join("\n");
  const footer =
    "\n\nErsetze in dieser Datei ausschließlich die o. g. Font-Referenzen durch die passenden Design-Token-Utilities (`font-display` = Funnel Display, `font-body` = Merriweather). Alle anderen Klassen, Attribute, Whitespace und der umliegende Text bleiben unverändert. Redundante `font-*`-Referenzen ersatzlos entfernen. Hero-Serif-Einsprengsel (`font-body font-normal italic` in Hero-H1) sind zulässige Ausnahmen.";
  return header + list + footer;
}

function CopyPromptButton({
  buildPrompt,
  label = "Fix-Prompt kopieren",
  variant = "secondary",
}: {
  buildPrompt: () => string;
  label?: string;
  variant?: "secondary" | "primary";
}) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);

  const base =
    variant === "primary"
      ? "bg-brand-button text-primary hover:opacity-90"
      : "border border-border bg-background text-primary hover:bg-secondary";

  return (
    <button
      type="button"
      onClick={async () => {
        const ok = await copyToClipboard(buildPrompt());
        if (ok) {
          setCopied(true);
          setFailed(false);
          window.setTimeout(() => setCopied(false), 2000);
        } else {
          setFailed(true);
          window.setTimeout(() => setFailed(false), 2500);
        }
      }}
      className={
        "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 font-display text-xs font-extrabold transition-opacity " +
        base
      }
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
          Kopiert!
        </>
      ) : failed ? (
        <>
          <Copy className="h-3.5 w-3.5" aria-hidden="true" />
          Fehler – erneut versuchen
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" aria-hidden="true" />
          {label}
        </>
      )}
    </button>
  );
}

function ContextBlock({ context }: { context: ViolationContext }) {
  return (
    <pre className="mt-3 min-w-0 max-w-full overflow-x-auto rounded-md border border-border bg-[#0f172a] p-0 font-mono text-[12px] leading-relaxed text-slate-100">
      <code className="block">
        {context.lines.map((line, i) => {
          const lineNo = context.startLine + i;
          const isOffending = i === context.offendingLineIndex;
          if (!isOffending) {
            return (
              <div key={i} className="flex">
                <span className="w-12 shrink-0 select-none border-r border-slate-700 px-2 py-0.5 text-right text-slate-500">
                  {lineNo}
                </span>
                <span className="whitespace-pre px-3 py-0.5 text-slate-300">
                  {line || " "}
                </span>
              </div>
            );
          }
          const col = Math.max(1, context.column) - 1;
          const before = line.slice(0, col);
          const match = line.slice(col, col + context.matchLength);
          const after = line.slice(col + context.matchLength);
          return (
            <div key={i} className="flex bg-brand-accent/15">
              <span className="w-12 shrink-0 select-none border-r border-brand-accent/60 px-2 py-0.5 text-right font-extrabold text-brand-accent">
                {lineNo}
              </span>
              <span className="whitespace-pre px-3 py-0.5 text-slate-50">
                {before}
                <mark className="rounded bg-brand-accent/60 px-0.5 text-slate-950">
                  {match || " "}
                </mark>
                {after}
              </span>
            </div>
          );
        })}
      </code>
    </pre>
  );
}

const RULE_TONE: Record<FontViolation["rule"], string> = {
  "font-family": "bg-brand-accent/15 text-brand-accent",
  "font-literal": "bg-brand-accent/15 text-brand-accent",
  "inline-style": "bg-brand-kommunikation/15 text-brand-kommunikation",
  "arbitrary-class": "bg-brand-kommunikation/15 text-brand-kommunikation",
  "foreign-font": "bg-destructive/15 text-destructive",
  "body-weight": "bg-destructive/15 text-destructive",
  "generic-family": "bg-brand-kommunikation/15 text-brand-kommunikation",
};

function ViolationCard({
  violation,
  ruleDescription,
  ruleRemedy,
}: {
  violation: FontViolation;
  ruleDescription: string;
  ruleRemedy: string;
}) {
  const v = violation;
  return (
    <li className="min-w-0 max-w-full overflow-hidden rounded-md border border-border bg-background p-4">
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={
                "rounded px-2 py-0.5 font-display text-[11px] font-extrabold uppercase tracking-wider " +
                RULE_TONE[v.rule]
              }
            >
              {RULE_LABEL[v.rule]}
            </span>
            <span className="font-mono text-[11px] text-muted-foreground">
              Zeile {v.line} · Spalte {v.column}
            </span>
            <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-[11px] text-secondary-foreground">
              {v.match}
            </code>
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <div className="rounded border border-brand-accent/30 bg-brand-accent/5 p-2.5">
              <p className="font-display text-[10px] font-extrabold uppercase tracking-wider text-brand-accent">
                Problem
              </p>
              <p className="mt-1 text-xs text-foreground">{ruleDescription}</p>
            </div>
            <div className="rounded border border-brand-wahlen/40 bg-brand-wahlen/10 p-2.5">
              <p className="font-display text-[10px] font-extrabold uppercase tracking-wider text-brand-wahlen">
                Sollte sein
              </p>
              <p className="mt-1 text-xs text-foreground">{ruleRemedy}</p>
            </div>
          </div>
        </div>
        <div className="shrink-0">
          <CopyPromptButton
            variant="primary"
            buildPrompt={() => buildViolationPrompt(v, ruleDescription, ruleRemedy)}
          />
        </div>
      </div>
      <ContextBlock context={v.context} />
    </li>
  );
}


function FontAuditPage() {
  const [result, setResult] = useState<FontAuditResult | null>(null);
  useEffect(() => {
    let cancelled = false;
    void runFontAudit().then((r) => {
      if (!cancelled) setResult(r);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!result) {
    return (
      <div className="p-8 text-sm text-muted-foreground">Schrift-Prüfung läuft …</div>
    );
  }

  const { scannedFiles, violations, allowlist, rules } = result;

  const rulesById = useMemo(() => {
    const map: Record<string, { description: string; remedy: string }> = {};
    for (const r of rules) map[r.id] = { description: r.description, remedy: r.remedy };
    return map;
  }, [rules]);

  const byFile = useMemo(() => {
    const map = new Map<string, FontViolation[]>();
    for (const v of violations) {
      const arr = map.get(v.file) ?? [];
      arr.push(v);
      map.set(v.file, arr);
    }
    return Array.from(map.entries());
  }, [violations]);

  const ok = violations.length === 0;

  return (
    <div className="min-w-0 max-w-full overflow-hidden">
      <h1 className="font-display text-3xl font-extrabold md:text-4xl">Font-Token-Audit</h1>
      <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
        Prüft, ob alle Schriftarten ausschließlich über Design-Tokens (CSS-Variablen oder
        Tailwind-Utilities) referenziert werden. Läuft live gegen den aktuellen Build.
      </p>

      <TypographyScale />

      <section
        className={
          "mt-8 flex items-start gap-4 rounded-lg border p-6 " +
          (ok
            ? "border-brand-wahlen bg-brand-wahlen/10"
            : "border-brand-accent bg-brand-accent/10")
        }
      >
        {ok ? (
          <CheckCircle2 className="mt-0.5 h-6 w-6 text-brand-wahlen" aria-hidden="true" />
        ) : (
          <AlertTriangle className="mt-0.5 h-6 w-6 text-brand-accent" aria-hidden="true" />
        )}
        <div>
          <p className="font-display text-xl font-extrabold text-primary">
            {ok ? "Alles in Ordnung" : `${violations.length} Abweichung(en) gefunden`}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {scannedFiles} Dateien gescannt · {allowlist.length} Pfade auf der Allowlist
          </p>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-xl font-extrabold text-primary">Regeln</h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {rules.map((r) => (
            <li
              key={r.id}
              className="rounded-md border border-border bg-background p-4 text-sm"
            >
              <p className="font-display text-xs font-extrabold uppercase tracking-wider text-brand-kommunikation">
                {r.id}
              </p>
              <p className="mt-1 text-muted-foreground">{r.description}</p>
              <p className="mt-2 text-xs text-foreground">
                <span className="font-display font-extrabold text-brand-wahlen">Sollte sein: </span>
                {r.remedy}
              </p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-xl font-extrabold text-primary">Allowlist</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Diese Pfade dürfen literale Font-Referenzen enthalten (Token-Definition,
          Standalone-Fallback, Google-Fonts-Loader, auto-generierte Dateien, die
          PDF-Erzeugung mit PDF-Standardschriften sowie die Audit-Logik selbst).
          Einzelne Zeilen lassen sich zusätzlich mit einem Kommentar
          <code className="mx-1">font-audit-ignore</code>
          in derselben oder einer der fünf Zeilen darüber ausnehmen — etwa für
          Pflichtangaben im Datenschutztext.
        </p>



        <ul className="mt-4 flex flex-wrap gap-2">
          {allowlist.map((p) => (
            <li
              key={p}
              className="rounded-md border border-border bg-secondary px-2.5 py-1 text-xs text-secondary-foreground"
            >
              <code>{p}</code>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-xl font-extrabold text-primary">Abweichungen</h2>
        {ok ? (
          <p className="mt-4 text-sm text-muted-foreground">
            Keine Abweichungen — sämtliche Font-Referenzen laufen über Tokens.
          </p>
        ) : (
          <ul className="mt-6 min-w-0 max-w-full space-y-6">
            {byFile.map(([file, items]) => (
              <li
                key={file}
                className="min-w-0 overflow-hidden rounded-lg border border-border bg-background p-5"
              >
                <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <FileWarning className="h-4 w-4 shrink-0 text-brand-accent" aria-hidden="true" />
                    <code className="truncate font-mono text-xs text-primary">{file}</code>
                    <span className="shrink-0 rounded-full bg-brand-accent/20 px-2 py-0.5 text-xs font-semibold text-brand-accent">
                      {items.length}
                    </span>
                  </div>
                  {items.length > 1 && (
                    <CopyPromptButton
                      label="Alle Fix-Prompts kopieren"
                      buildPrompt={() => buildFilePrompt(file, items, rulesById)}
                    />
                  )}
                </div>
                <ul className="mt-4 min-w-0 space-y-4">
                  {items.map((v, i) => {
                    const rule = rulesById[v.rule];
                    return (
                      <ViolationCard
                        key={`${v.line}-${v.column}-${i}`}
                        violation={v}
                        ruleDescription={rule?.description ?? ""}
                        ruleRemedy={rule?.remedy ?? ""}
                      />
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="mt-12 text-xs text-muted-foreground">
        ←{" "}
        <Link to="/admin" className="underline underline-offset-4 hover:text-foreground">
          Zurück zur Übersicht
        </Link>
      </p>
    </div>
  );
}
